import { createSlice } from "@reduxjs/toolkit";

import { initialState, PAY_DEPOSIT } from "./constants";

const isBeforeEbdCutoff = (ebdCutoff) => {
  const currentDate = new Date();
  try {
    // ensure timezone does not interfere with date comparison
    const ebdCutoffDate = new Date(
      new Date(ebdCutoff).toISOString().slice(0, -1)
    );
    // ebd is valid until the day after cutoff date
    ebdCutoffDate.setDate(ebdCutoffDate.getDate() + 1);
    return currentDate < ebdCutoffDate;
  } catch (e) {
    //console.log(ebdCutoff);
    return false;
  }
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addCamper(state, { payload: data }) {
      const { firstName, lastName, birthdate, grade, sex, shirtSize, camperKey } =
        data;
      if (
        state.campers.filter((camper) => camper.key === camperKey).length === 0
      ) {
        state.campers.push({
          key: camperKey,
          firstName,
          lastName,
          birthdate,
          grade,
          sex,
          shirtSize,
        });
      }
    },
    addPriorCamper(state, { payload: data }) {
      const {
        firstName,
        lastName,
        birthdate,
        sex,
        grade,
        shirtSize,
        camperKey,
        camperId,
      } = data;
      if (
        state.campers.filter((camper) => camper.key === camperKey).length === 0
      ) {
        state.campers.push({
          id: camperId,
          key: camperKey,
          firstName,
          lastName,
          birthdate,
          grade,
          sex,
          shirtSize,
        });
      }
    },
    addRegistration(state, { payload: newReg }) {
      const camperRegistrations = state.registrations.filter(
        (r) => r.camperKey === newReg.camperKey
      );
      if (camperRegistrations.filter((r) => r.id === newReg.id).length === 0) {
        state.registrations.push(newReg);
        state.isRegistrationAdded = true;
      }
    },
    updateCamper(state, { payload: data }) {
      const { firstName, lastName, birthdate, grade, sex, camperKey, shirtSize } =
        data;
      state.campers = state.campers.map((c) => {
        if (c.key === camperKey) {
          return {
            ...c,
            firstName,
            lastName,
            birthdate,
            grade,
            sex,
            shirtSize,
          };
        }
        return c;
      });
    },
    updateRegistration(state, { payload: newReg }) {
      //first find the old registration to get its session ID before updating
      const oldReg = state.registrations.find(r => r.key === newReg.key);

      //update the registration
      state.registrations = state.registrations.map((r) => {
        if (r.key === newReg.key) {
          return newReg;
        }
        return r;
      });

      //if we found the old registration and the session ID has changed, update associated products to use the new ID
      if (oldReg && oldReg.id !== newReg.id) {
        state.products = state.products.map(product => {
          if (product.camperKey === newReg.camperKey && product.programSessionId === oldReg.id) {
            return {
              ...product,
              programSessionId: newReg.id
            };
          }
          return product;
        });
      }
    },
    deleteCamper(state, { payload: camperKey }) {
      state.registrations = state.registrations.filter(
        (reg) => reg.camperKey !== camperKey
      );
      state.campers = state.campers.filter(
        (camper) => camper.key !== camperKey
      );
    },
    deleteRegistration(state, { payload: regKey }) {
      //remove products associated with this registration first
      state.registrations.forEach((reg) => {
        state.products = state.products.filter((product) => product.camperKey !== reg.camperKey || product.programSessionId !== reg.id);
      });
      state.registrations = state.registrations.filter(
        (reg) => reg.key !== regKey
      );
    },
    updatePayAmounts(state, { payload: programType }) {
      let total = 0;
      state.registrations = state.registrations.map((r) => {
        if (r.payOption === PAY_DEPOSIT) {
          r.payAmount = r.depositAmount;
        } else {
          let cost = r.totalCost;
          r.discounts.forEach((discount) => {
            cost -= discount.amount;
          });

          r.payAmount = cost;
        }

        // only add up the total for the selected program type
        // if (r.programType.includes(programType)) {
        //   total += r.payAmount;
        // }
        return r;
      });

      //state.totalPrice = total;
    },
    setTotalPrice(state, { payload: totalPrice }) {
      state.totalPrice = totalPrice;
    },
    updateRemainingBalances(state) {
      state.registrations = state.registrations.map((r) => {
        let remainingBalance = r.totalCost;
        if (r.payOption === PAY_DEPOSIT) {
          remainingBalance -= r.depositAmount;
          Object.entries(r.discounts).forEach(([key, value]) => {
            remainingBalance -= value;
          });
        } else {
          remainingBalance = 0;
        }

        r.remainingBalance = remainingBalance;
        return r;
      });
    },
    checkDiscounts(state) {
      state.registrations = state.registrations.map((reg) => {
        reg.discounts = [];
        const camper = state.campers.find((c) => c.key === reg.camperKey);
        // returning camper discount
        if (camper.id) {
          reg.discounts.return = reg.returnDiscount;
        }
        // early bird discount
        if (isBeforeEbdCutoff(reg.ebdCutoff)) {
          reg.discounts.ebd = reg.ebdAmount;
        }
        // special discount
        // if (reg.specialDiscount > 0) {
        //   reg.discounts.special = reg.specialDiscount;
        // }

        return reg;
      });
    },
    applyCoupon(state, { payload: discount }) {
      state.totalDiscount = discount.value;
      state.hasClemsonCoupon = !!discount.is_clemson_coupon;
      state.discountCode = discount.code;

      // Reset the immediate discount amount when applying a new coupon
      state.immediateDiscountAmount = 0;
    },
    setImmediateDiscountAmount(state, { payload: amount }) {
      state.immediateDiscountAmount = amount;
    },
    setDigitalSignature(state, { payload: signature }) {
      state.digitalSignature = signature;
    },
    clearIsRegistrationAdded(state) {
      state.isRegistrationAdded = false;
    },
    resetForProgramType(state, { payload: programType }) {
      //remove campers and registrations that are associated with the provided program type
      let filteredRegs = state.registrations.filter(
        (r) => !r.programType.includes(programType)
      );
      //only remove campers that have no remaining registrations in other program types
      state.campers = state.campers.filter((c) => {
        return filteredRegs.some((r) => r.camperKey === c.key);
      });
      //remove products which have a programSessionId and camperKey that have been removed from the cart
      state.products = state.products.filter((product) => {
        return filteredRegs.some((r) => r.camperKey === product.camperKey && r.id === product.programSessionId);
      });
      state.registrations = filteredRegs;
      state.totalPrice = 0;
      state.totalDiscount = 0;
      state.immediateDiscountAmount = 0;
      state.hasClemsonCoupon = false;
      state.discountCode = "";
      state.digitalSignature = "";
      state.isRegistrationAdded = false;
    },
    /*product looks like: {
      "id": 1,
      "name": "Test Product",
      "description": "This product is only a test. Don't buy it.",
      "price": 25.25,
      "image_path": null,
      "active": 1,
      "created_at": "2023-12-15T20:01:45.000000Z",
      "updated_at": "2023-12-16T23:18:32.000000Z",
      "pivot": {
          "program_id": 83,
          "product_id": 1
      }
  }
     */
    addProduct(state, { payload: product }) {
      state.products.push(product);
    },
    removeProduct(state, { payload: product }) {
      state.products = state.products.filter((p) => (p.id !== product.id) || (p.camperKey !== product.camperKey));
    },
    updateProductShipping(state, { payload: { product, camperKey, formState } }) {
        state.products = state.products.map((p) => {
            if (p.id === product.id && p.camperKey === camperKey) {
                return {
                    ...p,
                    shipping: formState,
                };
            }
            return p;
        });
    },
    removeAllProducts(state) {
      state.products = [];
    },
    removeProductFromSession(state, { payload: { camperKey, sessionId, productId } }) {
        console.log("Removing product from session" + camperKey + " " + sessionId + " " + productId);
        //remove the product with the given id from the session with the given camperKey and sessionId
        state.registrations = state.registrations.map((reg) => {
            if (reg.camperKey === camperKey && reg.id === sessionId) {
                reg.products = reg.products.filter((product) => product.id !== productId);
            }
            return reg;
        });
        //also remove the product from the cart
        state.products = state.products.filter((product) => product.id !== productId || product.camperKey !== camperKey || product.programSessionId !== sessionId);
    },
    reset: () => initialState,
  },
});

export const cartActions = cartSlice.actions;

export default cartSlice.reducer;
